// hooks/useVideoPlayerState.ts
import { useState, useEffect, useCallback, RefObject, MutableRefObject, Dispatch, SetStateAction } from "react";

interface UseVideoPlayerStateProps {
  initialTime: number;
  videoRefs: RefObject<(HTMLVideoElement | null)[]>;
  singleVideoRef: RefObject<HTMLVideoElement | null>;
  hasMultipleVideos: boolean;
}

export interface VideoPlayerState {
  duration: number;
  currentTime: number;
  sliderValue: number;
  isPlaying: boolean;
  isLoaded: boolean;
  setDuration: Dispatch<SetStateAction<number>>;
  setCurrentTime: Dispatch<SetStateAction<number>>;
  setSliderValue: Dispatch<SetStateAction<number>>;
  setIsPlaying: Dispatch<SetStateAction<boolean>>;
  setIsLoaded: Dispatch<SetStateAction<boolean>>;
  getVideos: () => HTMLVideoElement[];
  getPrimaryVideo: () => HTMLVideoElement | null;
}

export const SLIDER_PRECISION = 1000;

export const useVideoPlayerState = ({
  initialTime,
  videoRefs,
  singleVideoRef,
  hasMultipleVideos,
}: UseVideoPlayerStateProps): VideoPlayerState => {
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(initialTime);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [sliderValue, setSliderValue] = useState(0);

  const getVideos = useCallback(() => {
    // If we are in multi-video mode, use the videoRefs array.
    if (hasMultipleVideos) {
      return videoRefs.current?.filter(Boolean) as HTMLVideoElement[] || [];
    }
    // If we are in single-video mode, check if the single ref is populated.
    if (singleVideoRef.current) {
      // Return it as an array with one element, which is what the rest
      // of the code (like togglePlay) expects.
      return [singleVideoRef.current];
    }
    // If nothing is ready yet, return an empty array.
    return [];
  }, [hasMultipleVideos, videoRefs, singleVideoRef]);
  const getPrimaryVideo = useCallback(() => getVideos()[0] || null, [getVideos]);


  // This effect updates the slider's position when the video is playing
  useEffect(() => {
    if (!isLoaded || duration === 0) return;

    // The calculation now uses our precision constant
    const newSliderValue = (currentTime / duration) * SLIDER_PRECISION;
    setSliderValue(newSliderValue);

  }, [currentTime, isLoaded, duration]);

  return {
    duration,
    currentTime,
    sliderValue,
    isPlaying,
    isLoaded,
    setDuration,
    setCurrentTime,
    setSliderValue,
    setIsPlaying,
    setIsLoaded,
    getVideos,
    getPrimaryVideo,
  };
};